import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import './write-down.scss';

interface WriteDownProps {
  children: ReactNode;
  speed?: number;
  startDelay?: number;
  mode?: 'default' | 'ai-chat';
  className?: string;
  onComplete?: () => void;
}

export function WriteDown({
  children,
  speed = 60,
  startDelay = 200,
  mode = 'default',
  className = '',
  onComplete,
}: WriteDownProps) {
  const isAiChat = mode === 'ai-chat';
  const ref = useRef<HTMLParagraphElement>(null);

  const fullText = useMemo(() => {
    const text = typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.join('')
        : String(children);

    return text.replace(/\s+/g, ' ').trim();
  }, [children]);

  const [skipAnimation] = useState(false);
  const [committedIndex, setCommittedIndex] = useState(0);
  const committedIndexRef = useRef(0);
  const [ghostRange, setGhostRange] = useState<{ start: number; end: number } | null>(null);
  const ghostRangeRef = useRef<{ start: number; end: number } | null>(null);
  const [cursorPhase, setCursorPhase] = useState<'idle' | 'typing' | 'ghosting' | 'blinking' | 'hidden'>('idle');
  const [started, setStarted] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const completedRef = useRef(false);

  const notifyComplete = () => {
    if (completedRef.current) return;
    if (committedIndexRef.current < fullText.length) return;
    completedRef.current = true;
    onCompleteRef.current?.();
  };

  useEffect(() => {
    if (started) return undefined;
    setStarted(true);
    return undefined;
  }, [started]);

  useEffect(() => {
    if (skipAnimation || !started || !fullText) return undefined;
    setCursorPhase('typing');

    let timeoutId: number;

    const findWordStart = (index: number) => {
      let pos = index;
      while (pos > 0 && !/\s/.test(fullText[pos - 1])) {
        pos -= 1;
      }
      return pos;
    };

    const findWordEnd = (index: number) => {
      let pos = index;
      while (pos < fullText.length && !/\s/.test(fullText[pos])) {
        pos += 1;
      }
      return pos;
    };

    const shouldCreateGhost = (nextIndex: number) => {
      const start = findWordStart(nextIndex - 1);
      const end = findWordEnd(nextIndex - 1);
      const wordLength = end - start;
      const progress = nextIndex - start;
      return wordLength >= 6 && progress >= (Math.random() < 0.5 ? 3 : 4) && end > nextIndex;
    };

    const commitWord = (end: number) => {
      committedIndexRef.current = end;
      setCommittedIndex(end);
      ghostRangeRef.current = null;
      setGhostRange(null);
      setCursorPhase('typing');
    };

    const tick = () => {
      if (isAiChat) {
        const nextIndex = committedIndexRef.current;
        let end = findWordEnd(nextIndex);
        if (end === nextIndex && nextIndex < fullText.length) {
          end = findWordEnd(nextIndex + 1);
        }

        if (end >= fullText.length) {
          commitWord(fullText.length);
          setCursorPhase('hidden');
          notifyComplete();
          return;
        }

        commitWord(end);
        timeoutRef.current = window.setTimeout(tick, speed + Math.random() * 30);
        return;
      }

      if (ghostRangeRef.current) {
        commitWord(ghostRangeRef.current.end);
        timeoutRef.current = window.setTimeout(tick, speed * 1.5);
        return;
      }

      const nextIndex = committedIndexRef.current + 1;
      if (nextIndex > fullText.length) {
        setCursorPhase('blinking');
        return;
      }

      committedIndexRef.current = nextIndex;
      setCommittedIndex(nextIndex);
      const start = findWordStart(nextIndex - 1);
      const end = findWordEnd(nextIndex - 1);
      const progress = nextIndex - start;

      if (!/\s/.test(fullText[nextIndex - 1]) && shouldCreateGhost(nextIndex) && end > nextIndex) {
        ghostRangeRef.current = { start: nextIndex, end };
        setGhostRange({ start: nextIndex, end });
        setCursorPhase('ghosting');
        timeoutRef.current = window.setTimeout(tick, speed * 1.25 + Math.random() * 20);
        return;
      }

      if (nextIndex < fullText.length) {
        timeoutRef.current = window.setTimeout(tick, speed + Math.random() * 30);
      } else {
        setCursorPhase('blinking');
        notifyComplete();
      }
    };

    timeoutRef.current = window.setTimeout(tick, startDelay);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [started, fullText, speed, startDelay]);

  useEffect(() => {
    if (cursorPhase !== 'blinking') return undefined;

    const timeoutId = window.setTimeout(() => {
      setCursorPhase('hidden');
    }, 2400);

    return () => window.clearTimeout(timeoutId);
  }, [cursorPhase]);

  const committedText = fullText.slice(0, skipAnimation ? fullText.length : committedIndex);
  const ghostText = !isAiChat && !skipAnimation && ghostRange ? fullText.slice(ghostRange.start, ghostRange.end) : '';

  const stateClass = isAiChat
    ? 'write-down--ai-chat'
    : cursorPhase === 'typing' || cursorPhase === 'ghosting'
      ? 'write-down--typing'
      : cursorPhase === 'blinking'
        ? 'write-down--blinking'
        : '';

  return (
    <p ref={ref} className={`write-down ${stateClass} ${className}`.trim()}>
      {committedText}
      {ghostText && <span className="write-down__ghost">{ghostText}</span>}
    </p>
  );
}
